import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { getWallet, type CapabilityCosts, type TopUpPack } from '../services/walletService';
import { useAuth } from './AuthContext';

/**
 * Wisetree Wallet — the single WP (Wisetree Points) balance, surfaced to the usage bar on the
 * profile and to the wallet page. Refreshes on login and exposes `refresh()` so a charge or
 * top-up can re-pull the latest balance.
 */
interface WalletContextType {
  /** The one spendable balance, in WP. */
  wp: number;
  /** The plan's WP balance cap — the usage-bar denominator (one starter pack; top-ups may exceed it). */
  cap: number;
  /**
   * True when this wallet has never held a credit — nothing spent, nothing ever credited. New accounts start
   * at 0 (no welcome grant since 2026-09-27), so an empty wallet here is a creator who has not begun, not one
   * who ran out; copy must invite rather than say they used everything.
   */
  neverFunded: boolean;
  /** The first-purchase starter pack while this account may buy it (server-decided), else null — for any
   *  surface that meets an empty wallet (TRY-4). */
  starterPack: TopUpPack | null;
  plan: string;
  /** ISO of the last monthly subscriber grant. */
  lastMonthlyAt: string | null;
  /** ISO of the next Stripe renewal/charge date (subscription anniversary) — the accurate date to show,
   *  sourced from Stripe. Null for free plans or before the webhook has synced the period. */
  renewsAt: string | null;
  /** The server cost model. Used for sizing a spend CONFIRMATION — never as an ambient price tag. */
  capabilities: CapabilityCosts;
  loading: boolean;
  refresh: () => Promise<void>;
}

// The free-plan cap as the safe default until the wallet loads (matches backend PLAN_BALANCE_CAP.free).
const DEFAULT_CAP = 2400;
const DEFAULT: WalletContextType = { wp: 0, cap: DEFAULT_CAP, neverFunded: false, starterPack: null, plan: 'free', lastMonthlyAt: null, renewsAt: null, capabilities: {}, loading: true, refresh: async () => {} };
const WalletContext = createContext<WalletContextType>(DEFAULT);
export const useWallet = () => useContext(WalletContext);

/** Waits between attempts at the FIRST read for a signed-in user. A cold function or a transient error on that
 *  read used to leave the default 0 on screen as if it were the balance (BUGS.md 2026-09-26). */
export const FIRST_READ_RETRY_MS = [1000, 3000, 9000];

export const WalletProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const uid = user?.id || null;
  const [wp, setWp] = useState(0);
  const [cap, setCap] = useState(DEFAULT_CAP);
  const [neverFunded, setNeverFunded] = useState(false);
  const [starterPack, setStarterPack] = useState<TopUpPack | null>(null);
  const [plan, setPlan] = useState('free');
  const [lastMonthlyAt, setLastMonthlyAt] = useState<string | null>(null);
  const [renewsAt, setRenewsAt] = useState<string | null>(null);
  const [capabilities, setCapabilities] = useState<CapabilityCosts>({});
  /**
   * TRUE UNTIL A REAL READ HAS LANDED for the signed-in user (BUGS.md 2026-09-26: /wallet told a brand-new
   * creator they had used all their credits while the dashboard said 1,400). `loading` used to go false while
   * auth was still restoring (no user yet) and never came back on, so every surface drew the placeholder 0 as
   * the balance — "0 WP, you have used all your credits" — until the first read returned, and for good if that
   * read failed. The balance is now either a number the server said, or unknown ("…"); never an invented 0.
   */
  const [loading, setLoading] = useState(true);
  /** Whose balance is on screen, so a slow answer for a previous account can never land on the next one. */
  const uidRef = useRef<string | null>(uid);
  uidRef.current = uid;

  const read = useCallback(async (): Promise<boolean> => {
    const asked = uidRef.current;
    try {
      const { wallet, capabilities: c, cap: planCap, starterPack: starter, starterPackEligible } = await getWallet();
      if (asked !== uidRef.current) return false;
      // The backend coerces pre-merge wallets into `wp`; default to 0 defensively anyway.
      setWp(wallet.wp ?? 0);
      if (planCap) setCap(planCap);
      setNeverFunded(!(wallet.wp > 0) && !(wallet.wp_spent > 0) && !(wallet.wp_topped > 0));
      setStarterPack(starterPackEligible && starter ? starter : null);
      setPlan(wallet.plan);
      setLastMonthlyAt(wallet.last_monthly_at ?? null);
      setRenewsAt(wallet.renews_at ?? null);
      setCapabilities(c || {});
      setLoading(false);
      return true;
    } catch {
      return false;   // keep the last KNOWN balance on a transient error; unknown stays unknown
    }
  }, []);

  const refresh = useCallback(async () => { await read(); }, [read]);

  // A new account (or signing out) starts from UNKNOWN, not from whatever the last one held. The first read
  // retries a few times: a creator's first visit is exactly when the function is coldest.
  useEffect(() => {
    setWp(0); setCap(DEFAULT_CAP); setNeverFunded(false); setStarterPack(null); setPlan('free'); setLastMonthlyAt(null); setRenewsAt(null);
    setLoading(true);
    if (!uid) return undefined;
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const attempt = async (n: number) => {
      if (!alive || await read() || !alive || n >= FIRST_READ_RETRY_MS.length) return;
      timer = setTimeout(() => { void attempt(n + 1); }, FIRST_READ_RETRY_MS[n]);
    };
    void attempt(0);
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [uid, read]);

  return (
    <WalletContext.Provider value={{ wp, cap, neverFunded, starterPack, plan, lastMonthlyAt, renewsAt, capabilities, loading, refresh }}>
      {children}
    </WalletContext.Provider>
  );
};
